import { FC, memo, ReactElement } from 'react';
import { Col, Dropdown, Menu, Typography } from 'antd';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { useAppSelector, useTranslate } from 'hooks';

import { logout } from 'features/authorization/actions';

import { ClientStatus, ClientRole } from 'utils/constants';

import { AccountService } from 'api';

import { AdminMessage } from '../AdminMessage';

import Utils from 'utils/utils';

import { selectOverview, selectUser } from 'selectors';

import { ReactComponent as ProfileIcon } from 'assets/icons/user.svg';
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow-select.svg';
import { ReactComponent as AvatarIcon } from 'assets/icons/avatar.svg';
import { ReactComponent as ExitIcon } from 'assets/icons/exit.svg';
import { ReactComponent as SettingsIcon } from 'assets/icons/settings.svg';

import styles from './index.module.scss';
import cn from 'classnames';

type ProfileButtonProps = {
	email: string;
	children?: ReactElement;
	isAccount?: boolean;
};

const ProfileButton: FC<ProfileButtonProps> = memo(({ isAccount, email }) => {
	const overview = useAppSelector(selectOverview);
	const user = useAppSelector(selectUser);
	const isAdmin = useAppSelector((state) => state.cookies.isAdmin);

	const { userIdText, logOutText, settingsText } = useTranslate([
		'userIdText',
		'logOutText',
		'settingsText',
	]);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const avatarTitle =
		user.userType === ClientRole.COMPANY
			? user.companyName || email
			: user.firstName
			? `${user.firstName} ${user.lastName}`
			: email;

	return (
		<div className={styles.profileButton}>
			{isAdmin && <AdminMessage />}

			<Col className={styles.dropdown}>
				<Dropdown
					placement="bottomRight"
					overlayClassName={styles.dropdownContent}
					dropdownRender={() => (
						<Menu>
							<div className={styles.mobile}>
								<Typography.Text className={styles.userId}>{avatarTitle}</Typography.Text>
								<Typography.Text type="secondary" className={styles.userId}>
									{Utils.moneyFormat(+overview.availableMoney || 0)}
								</Typography.Text>
							</div>

							{user.accountId && (
								<Typography.Text className={styles.userId} type="secondary">
									{userIdText}: {user.accountId}
								</Typography.Text>
							)}

							<div className={styles.line} />
							{user.status === ClientStatus.ACTIVE && (
								<Menu.Item key="settings">
									<SettingsIcon />
									<Link to="/settings">{settingsText}</Link>
								</Menu.Item>
							)}
							<Menu.Item
								key="logout"
								onClick={() => {
									AccountService.logout().finally(() => {
										dispatch(logout());
										navigate('/');
									});
								}}
							>
								<ExitIcon />
								{logOutText}
							</Menu.Item>
						</Menu>
					)}
					trigger={['click']}
				>
					<div>
						{isAccount ? (
							<AvatarIcon className={styles.avatar} />
						) : (
							<Col className={styles.icon}>
								<ProfileIcon />
							</Col>
						)}

						<div className={styles.column}>
							<div className={cn(styles.email, { [styles.emailAccount]: isAccount })}>
								<Typography.Text>{avatarTitle}</Typography.Text>
								<ArrowIcon className={styles.arrow} />
							</div>
							{overview.availableMoney && (
								<Typography.Text className={styles.availableMoney} type="secondary">
									{Utils.moneyFormat(+overview.availableMoney)}
								</Typography.Text>
							)}
						</div>
					</div>
				</Dropdown>
			</Col>
		</div>
	);
});

export { ProfileButton };
